import { useId, useRef, useState, type DragEvent } from 'react';
import { FileUp } from 'lucide-react';
import { cn } from './cn';
import { tailleLisible } from './tailleLisible';

/** Une zone de dépôt : glisser un fichier, ou cliquer pour le choisir. */
export function ChampFichier({
  libelle,
  fichier,
  onChoisir,
  erreur,
  accepter = '.pdf,.jpg,.jpeg,.png,.heic,.webp',
}: {
  libelle: string;
  fichier: File | null;
  onChoisir: (fichier: File | null) => void;
  erreur?: string | undefined;
  accepter?: string;
}) {
  const id = useId();
  const champ = useRef<HTMLInputElement>(null);
  const [survol, setSurvol] = useState(false);

  function deposer(evenement: DragEvent) {
    evenement.preventDefault();
    setSurvol(false);
    onChoisir(evenement.dataTransfer.files[0] ?? null);
  }

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium">
        {libelle}
      </label>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setSurvol(true);
        }}
        onDragLeave={() => {
          setSurvol(false);
        }}
        onDrop={deposer}
        onClick={() => champ.current?.click()}
        className={cn(
          'flex cursor-pointer items-center gap-3 rounded-xl border border-dashed px-4 py-4 transition',
          survol
            ? 'border-primary bg-primary-subtle'
            : 'border-border bg-surface-sunken hover:border-primary-border',
          erreur && 'border-danger-border'
        )}
      >
        <span className="grid size-10 place-items-center rounded-xl bg-primary-subtle text-primary">
          <FileUp className="size-5" aria-hidden />
        </span>
        <span className="min-w-0 flex-1 text-[13px]">
          {fichier ? (
            <>
              <span className="block truncate font-medium">{fichier.name}</span>
              <span className="text-xs text-muted">{tailleLisible(fichier.size)}</span>
            </>
          ) : (
            <>
              <span className="block font-medium">Déposer un fichier ou cliquer pour choisir</span>
              <span className="text-xs text-muted">PDF ou photo</span>
            </>
          )}
        </span>
      </div>
      <input
        ref={champ}
        id={id}
        type="file"
        accept={accepter}
        className="sr-only"
        onChange={(e) => {
          onChoisir(e.target.files?.[0] ?? null);
        }}
      />
      {erreur ? <p className="text-xs text-danger">{erreur}</p> : null}
    </div>
  );
}
